import { Injectable, inject } from '@angular/core';
import { ModalController } from '@ionic/angular/standalone';
import { OverlayEventDetail } from '@ionic/core';
import { REFLECTION_MODAL } from '@lyceo/shared/plan/util';
import { TaskReflectionWithNote } from '@lyceo/shared/plan/util-models';
import { IonicComponentRef } from '@lyceo/shared/util-models';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, exhaustMap, filter, map, of } from 'rxjs';
import { TaskService } from '../../task.service';
import { ReflectionModalActions, TaskActions } from './task.actions';
import { PlanPageActions } from '../plan/plan.actions';
import { ApplicationFacade } from '@lyceo/shared/data-access';
import { isDefined } from '@lyceo/shared/util';
import { TranslocoService } from '@jsverse/transloco';
import { PupilContextFacade } from '@lyceo/shared/pupil-context/data-access';

@Injectable()
export class ReflectionModalEffects {
  private readonly actions$ = inject(Actions);
  private readonly modalController: ModalController = inject(ModalController);
  private readonly taskService: TaskService = inject(TaskService);
  private readonly reflectionModal =
    inject<IonicComponentRef>(REFLECTION_MODAL);
  private readonly applicationFacade: ApplicationFacade =
    inject(ApplicationFacade);
  private readonly pupilContextFacade: PupilContextFacade =
    inject(PupilContextFacade);
  private readonly translocoService: TranslocoService =
    inject(TranslocoService);

  public loadTaskFullPupil$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(PlanPageActions.pupilCompleteTaskClicked),
      filter(
        (action) =>
          action.value && !!action.task.parentTaskId && action.showReflection,
      ),
      exhaustMap((action) => {
        const homiPersonId: string | undefined =
          this.pupilContextFacade.homiPersonId();
        if (!isDefined(homiPersonId)) {
          return of(
            TaskActions.getFailure({
              error: new Error('Pupil ID is not defined'),
            }),
          );
        }

        return this.taskService.get(homiPersonId, action.task.id, true).pipe(
          map((task) => TaskActions.getSuccess({ task: task })),
          catchError((error: Error) => of(TaskActions.getFailure({ error }))),
        );
      }),
    );
  });

  public openReflectionModalPupil$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(PlanPageActions.pupilCompleteTaskClicked),
      filter(
        (action) =>
          action.value && !!action.task.parentTaskId && action.showReflection,
      ),
      exhaustMap(async (action) => {
        const modal: HTMLIonModalElement = await this.modalController.create({
          component: this.reflectionModal,
          componentProps: {
            task: action.task,
          },
        });
        await modal.present();
        return modal.onDidDismiss();
      }),
      map((data: OverlayEventDetail<TaskReflectionWithNote>) => {
        if (data.data) {
          return ReflectionModalActions.updateReflection({
            request: data.data,
          });
        }

        return ReflectionModalActions.modalClosed();
      }),
    );
  });

  public updateReflection$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ReflectionModalActions.updateReflection),
      exhaustMap((action) => {
        const homiPersonId: string | undefined =
          this.pupilContextFacade.homiPersonId();
        if (!isDefined(homiPersonId)) {
          return of(
            ReflectionModalActions.updateReflectionFailure({
              error: new Error('Pupil ID is not defined'),
            }),
          );
        }

        return this.taskService
          .updateReflection(homiPersonId, action.request.reflection)
          .pipe(
            map((task) =>
              ReflectionModalActions.updateReflectionSuccess({ task }),
            ),
            catchError((error: Error) =>
              of(ReflectionModalActions.updateReflectionFailure({ error })),
            ),
          );
      }),
    );
  });

  public updateNote$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ReflectionModalActions.updateReflection),
      filter((action) => !!action.request.note),
      exhaustMap((action) =>
        this.taskService.upsertNote(action.request.note).pipe(
          map((note) => ReflectionModalActions.upsertNoteSuccess({ note })),
          catchError((error: Error) =>
            of(ReflectionModalActions.upsertNoteFailure({ error })),
          ),
        ),
      ),
    );
  });

  public updateReflectionFailure$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(ReflectionModalActions.updateReflectionFailure),
        map(() => {
          this.applicationFacade.showToaster({
            type: 'error',
            message: this.translocoService.translate(
              'plan.toasts.reflection_save_failure',
            ),
          });
        }),
      );
    },
    { dispatch: false },
  );

  public updateNoteFailure$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(ReflectionModalActions.upsertNoteFailure),
        map(() => {
          this.applicationFacade.showToaster({
            type: 'error',
            message: this.translocoService.translate(
              'plan.toasts.reflection_note_save_failure',
            ),
          });
        }),
      );
    },
    { dispatch: false },
  );
}
